import React from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { useAppUi } from '../app/useAppUi';

export function ProvidersPage() {
  const { t } = useAppUi();
  const [loading, setLoading] = React.useState(true);
  const [savingKey, setSavingKey] = React.useState('');
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/providers');
      const data = unwrapApiResponse(res);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const save = React.useCallback(async (key, patch) => {
    setSavingKey(key);
    try {
      await http.put(`/admin/providers/${key}`, patch);
      await fetchData();
    } finally {
      setSavingKey('');
    }
  }, [fetchData]);

  const columns = React.useMemo(
    () => [
    { field: 'key', headerName: t('providers.colKey'), width: 160 },
    {
      field: 'label',
      headerName: t('providers.colLabel'),
      flex: 1,
      minWidth: 220,
      renderCell: (p) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
            {p.value || '—'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'enabled',
      headerName: t('providers.colEnabled'),
      width: 120,
      sortable: false,
      renderCell: (p) => (
        <Switch
          checked={Boolean(p.value)}
          disabled={savingKey === p.row.key}
          onChange={(e) => save(p.row.key, { enabled: e.target.checked })}
        />
      ),
    },
    {
      field: 'priority',
      headerName: t('providers.colPriority'),
      width: 140,
      renderCell: (p) => (
        <TextField
          type="number"
          size="small"
          defaultValue={p.value ?? 0}
          disabled={savingKey === p.row.key}
          onBlur={(e) => {
            const next = Number(e.target.value);
            if (!Number.isFinite(next)) return;
            if (next === (p.value ?? 0)) return;
            save(p.row.key, { priority: next });
          }}
          sx={{ width: 110 }}
        />
      ),
    },
    { field: 'updated_at', headerName: t('providers.colUpdated'), width: 170 },
  ],
    [t, savingKey, save]
  );

  return (
    <Box>
      <PageHeader
        title={t('providers.title')}
        subtitle={t('providers.subtitle')}
        right={
          <Button variant="outlined" onClick={fetchData} disabled={loading}>
            {t('common.refresh')}
          </Button>
        }
      />

      <ErrorBanner error={error} />

      <Paper
        elevation={0}
        sx={{
          p: 0,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
        }}
      >
        <Box sx={{ height: 640 }}>
          <DataGrid
            rows={rows}
            getRowId={(r) => r.key}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
          />
        </Box>
      </Paper>
    </Box>
  );
}

